export type Language = 'en' | 'pt' | 'pt-BR' | 'es' | 'fr' | 'de';

export interface Translations {
  hero: {
    headline: string;
    subtext: string;
    ctaButton: string;
    rating: string;
    players: string;
    partyFavorite: string;
  };
  features: {
    title: string;
    items: Array<{ emoji: string; text: string }>;
  };
  testimonials: {
    title: string;
    items: Array<{ emoji: string; text: string; author: string }>;
  };
  gameModes: {
    title: string;
    modes: Array<{ emoji: string; name: string; description: string }>;
  };
  languages: {
    title: string;
    description: string;
  };
  download: {
    title: string;
    description: string;
  };
  meta: {
    title: string;
    description: string;
    keywords: string;
  };
}

export const translations: Record<Language, Translations> = {
  en: {
    hero: {
      headline: 'Truth or Dare? Turns any night into chaos 🔥',
      subtext: 'A game to be played with your friends or with your partner. From fun to extreme — free to download.',
      ctaButton: 'Play Now - Free',
      rating: '4.8 Rating',
      players: '50,000+ Players',
      partyFavorite: 'Party Favorite',
    },
    features: {
      title: 'Make nights unforgettable',
      items: [
        { emoji: '😂', text: 'Laugh with friends' },
        { emoji: '❤️', text: 'Perfect for couples' },
        { emoji: '🔥', text: 'Hot & extreme dares' },
        { emoji: '🍺', text: 'Ideal for parties' },
      ],
    },
    testimonials: {
      title: 'What players say',
      items: [
        { emoji: '😂', text: 'We played this at a party and everyone loved it 😂', author: 'Sofia' },
        { emoji: '🔥', text: 'Perfect for couples night 🔥', author: 'Andre' },
        { emoji: '💀', text: 'This game gets crazy fast 💀', author: 'T...' },
      ],
    },
    gameModes: {
      title: 'Choose your vibe',
      modes: [
        { emoji: '😀', name: 'Friends Mode', description: 'Fun & light dares' },
        { emoji: '❤️', name: 'Couples Mode', description: 'Romantic & spicy' },
        { emoji: '🔥', name: 'Hot Mode', description: 'Bold & daring' },
        { emoji: '💀', name: 'Extreme Mode', description: 'Only for the brave' },
      ],
    },
    languages: {
      title: 'Available in multiple languages',
      description: 'Spin The Truth is available in the following languages:',
    },
    download: {
      title: 'Download Now',
      description: 'Get Spin The Truth on your mobile device. Available on iOS.',
    },
    meta: {
      title: 'Spin The Truth - Play with Friends | iOS App',
      description: 'Spin The Truth - Play with friends or partners. Get to know each other. Maximize the fun. Available on iOS.',
      keywords: 'spin the truth, truth or dare, mobile app, iOS, friends, party game',
    },
  },
  pt: {
    hero: {
      headline: 'Verdade ou Consequência? Transforma qualquer noite em caos 🔥',
      subtext: 'Um jogo para jogar com amigos ou com o teu parceiro. Do divertido ao extremo — download grátis.',
      ctaButton: 'Jogar Agora - Grátis',
      rating: '4.8 Avaliação',
      players: '50.000+ Jogadores',
      partyFavorite: 'Favorito de Festas',
    },
    features: {
      title: 'Torna as noites inesquecíveis',
      items: [
        { emoji: '😂', text: 'Gargalhadas com amigos' },
        { emoji: '❤️', text: 'Perfeito para casais' },
        { emoji: '🔥', text: 'Desafios quentes e extremos' },
        { emoji: '🍺', text: 'Ideal para festas' },
      ],
    },
    testimonials: {
      title: 'O que os jogadores dizem',
      items: [
        { emoji: '😂', text: 'Jogamos numa festa e todos adoraram 😂', author: 'Sofia' },
        { emoji: '🔥', text: 'Perfeito para noite de casais 🔥', author: 'Andre' },
        { emoji: '💀', text: 'Este jogo fica louco rapidamente 💀', author: 'T...' },
      ],
    },
    gameModes: {
      title: 'Escolhe a vibe',
      modes: [
        { emoji: '😀', name: 'Modo Amigos', description: 'Diversão e desafios leves' },
        { emoji: '❤️', name: 'Modo Casais', description: 'Romântico e picante' },
        { emoji: '🔥', name: 'Modo Quente', description: 'Ousado e arrojado' },
        { emoji: '💀', name: 'Modo Extremo', description: 'Apenas para os corajosos' },
      ],
    },
    languages: {
      title: 'Disponível em vários idiomas',
      description: 'Está disponível nos seguintes idiomas:',
    },
    download: {
      title: 'Download',
      description: 'Faz o download do Spin The Truth no seu dispositivo móvel. Disponível no iOS.',
    },
    meta: {
      title: 'Spin The Truth - Jogue com Amigos | App iOS',
      description: 'Spin The Truth - Jogue com amigos ou parceiros. Conheçam-se melhor. Maximize a diversão. Disponível no iOS.',
      keywords: 'spin the truth, verdade ou consequência, aplicativo móvel, iOS, amigos, jogo de festa',
    },
  },
  es: {
    hero: {
      headline: '¿Verdad o Reto? - Convierte cualquier noche en caos 🔥',
      subtext: 'Un juego para jugar con tus amigos o con tu pareja. De divertido a extremo — gratis para descargar.',
      ctaButton: 'Jugar Ahora - Gratis',
      rating: '4.8 Calificación',
      players: '50.000+ Jugadores',
      partyFavorite: 'Favorito de Fiestas',
    },
    features: {
      title: 'Haz las noches inolvidables',
      items: [
        { emoji: '😂', text: 'Ríe con amigos' },
        { emoji: '❤️', text: 'Perfecto para parejas' },
        { emoji: '🔥', text: 'Desafíos calientes y extremos' },
        { emoji: '🍺', text: 'Ideal para fiestas' },
      ],
    },
    testimonials: {
      title: 'Lo que dicen los jugadores',
      items: [
        { emoji: '😂', text: 'Jugamos esto en una fiesta y a todos les encantó 😂', author: 'Sofia' },
        { emoji: '🔥', text: 'Perfecto para noche de parejas 🔥', author: 'Andre' },
        { emoji: '💀', text: 'Este juego se vuelve loco rápido 💀', author: 'T...' },
      ],
    },
    gameModes: {
      title: 'Elige tu ambiente',
      modes: [
        { emoji: '😀', name: 'Modo Amigos', description: 'Diversión y desafíos ligeros' },
        { emoji: '❤️', name: 'Modo Parejas', description: 'Romántico y picante' },
        { emoji: '🔥', name: 'Modo Caliente', description: 'Audaz y atrevido' },
        { emoji: '💀', name: 'Modo Extremo', description: 'Solo para los valientes' },
      ],
    },
    languages: {
      title: 'Disponible en varios idiomas',
      description: 'Spin The Truth está disponible en los siguientes idiomas:',
    },
    download: {
      title: 'Descarga Ahora',
      description: 'Obtén Spin The Truth en tu dispositivo móvil. Disponible en iOS.',
    },
    meta: {
      title: 'Spin The Truth - Juega con Amigos | App iOS',
      description: 'Spin The Truth - Juega con amigos o parejas. Conócense mejor. Maximiza la diversión. Disponible en iOS.',
      keywords: 'spin the truth, verdad o reto, aplicación móvil, iOS, amigos, juego de fiesta',
    },
  },
  fr: {
    hero: {
      headline: 'Vérité ou Action ? Transforme n\'importe quelle nuit en chaos 🔥',
      subtext: 'Un jeu à jouer avec tes amis ou avec ton partenaire. Du fun à l\'extrême — gratuit à télécharger.',
      ctaButton: 'Jouer Maintenant - Gratuit',
      rating: '4.8 Note',
      players: '50 000+ Joueurs',
      partyFavorite: 'Favori des Fêtes',
    },
    features: {
      title: 'Rendez les nuits inoubliables',
      items: [
        { emoji: '😂', text: 'Riez avec des amis' },
        { emoji: '❤️', text: 'Parfait pour les couples' },
        { emoji: '🔥', text: 'Défis chauds et extrêmes' },
        { emoji: '🍺', text: 'Idéal pour les fêtes' },
      ],
    },
    testimonials: {
      title: 'Ce que disent les joueurs',
      items: [
        { emoji: '😂', text: 'Nous avons joué à ça lors d\'une fête et tout le monde a adoré 😂', author: 'Sofia' },
        { emoji: '🔥', text: 'Parfait pour une soirée en couple 🔥', author: 'Andre' },
        { emoji: '💀', text: 'Ce jeu devient fou rapidement 💀', author: 'T...' },
      ],
    },
    gameModes: {
      title: 'Choisissez votre ambiance',
      modes: [
        { emoji: '😀', name: 'Mode Amis', description: 'Amusement et défis légers' },
        { emoji: '❤️', name: 'Mode Couples', description: 'Romantique et épicé' },
        { emoji: '🔥', name: 'Mode Chaud', description: 'Audacieux et osé' },
        { emoji: '💀', name: 'Mode Extrême', description: 'Seulement pour les courageux' },
      ],
    },
    languages: {
      title: 'Disponible en plusieurs langues',
      description: 'Spin The Truth est disponible dans les langues suivantes:',
    },
    download: {
      title: 'Téléchargez Maintenant',
      description: 'Obtenez Spin The Truth sur votre appareil mobile. Disponible sur iOS.',
    },
    meta: {
      title: 'Spin The Truth - Jouez avec des Amis | App iOS',
      description: 'Spin The Truth - Jouez avec des amis ou des partenaires. Apprenez à vous connaître. Maximisez le plaisir. Disponible sur iOS.',
      keywords: 'spin the truth, vérité ou défi, application mobile, iOS, amis, jeu de fête',
    },
  },
  de: {
    hero: {
      headline: 'Wahrheit oder Pflicht? Verwandelt jede Nacht in Chaos 🔥',
      subtext: 'Ein Spiel zum Spielen mit deinen Freunden oder mit deinem Partner. Von spaßig bis extrem — kostenlos zum Download.',
      ctaButton: 'Jetzt Spielen - Kostenlos',
      rating: '4.8 Bewertung',
      players: '50.000+ Spieler',
      partyFavorite: 'Partyliebling',
    },
    features: {
      title: 'Mache Nächte unvergesslich',
      items: [
        { emoji: '😂', text: 'Lache mit Freunden' },
        { emoji: '❤️', text: 'Perfekt für Paare' },
        { emoji: '🔥', text: 'Heiße und extreme Mutproben' },
        { emoji: '🍺', text: 'Ideal für Partys' },
      ],
    },
    testimonials: {
      title: 'Was Spieler sagen',
      items: [
        { emoji: '😂', text: 'Wir haben das auf einer Party gespielt und alle haben es geliebt 😂', author: 'Sofia' },
        { emoji: '🔥', text: 'Perfekt für Paarabende 🔥', author: 'Andre' },
        { emoji: '💀', text: 'Dieses Spiel wird schnell verrückt 💀', author: 'T...' },
      ],
    },
    gameModes: {
      title: 'Wähle deine Stimmung',
      modes: [
        { emoji: '😀', name: 'Freunde-Modus', description: 'Spaß und leichte Mutproben' },
        { emoji: '❤️', name: 'Paare-Modus', description: 'Romantisch und scharf' },
        { emoji: '🔥', name: 'Heißer Modus', description: 'Mutig und gewagt' },
        { emoji: '💀', name: 'Extremer Modus', description: 'Nur für die Mutigen' },
      ],
    },
    languages: {
      title: 'Verfügbar in mehreren Sprachen',
      description: 'Spin The Truth ist in den folgenden Sprachen verfügbar:',
    },
    download: {
      title: 'Jetzt Herunterladen',
      description: 'Holen Sie sich Spin The Truth auf Ihr mobiles Gerät. Verfügbar auf iOS.',
    },
    meta: {
      title: 'Spin The Truth - Spiele mit Freunden | iOS App',
      description: 'Spin The Truth - Spiele mit Freunden oder Partnern. Lernt euch besser kennen. Maximiert den Spaß. Verfügbar auf iOS.',
      keywords: 'spin the truth, wahrheit oder pflicht, mobile app, iOS, freunde, partyspiel',
    },
  },
  'pt-BR': {
    hero: {
      headline: 'Verdade ou Desafio? Transforma qualquer noite em caos 🔥',
      subtext: 'Um jogo para jogar com seus amigos ou com seu parceiro. Do divertido ao extremo — grátis para baixar.',
      ctaButton: 'Jogar Agora - Grátis',
      rating: '4.8 Avaliação',
      players: '50.000+ Jogadores',
      partyFavorite: 'Favorito de Festas',
    },
    features: {
      title: 'Torne as noites inesquecíveis',
      items: [
        { emoji: '😂', text: 'Ria com amigos' },
        { emoji: '❤️', text: 'Perfeito para casais' },
        { emoji: '🔥', text: 'Desafios quentes e extremos' },
        { emoji: '🍺', text: 'Ideal para festas' },
      ],
    },
    testimonials: {
      title: 'O que os jogadores dizem',
      items: [
        { emoji: '😂', text: 'Jogamos isso numa festa e todos adoraram 😂', author: 'Sofia' },
        { emoji: '🔥', text: 'Perfeito para noite de casais 🔥', author: 'Andre' },
        { emoji: '💀', text: 'Este jogo fica louco rapidamente 💀', author: 'T...' },
      ],
    },
    gameModes: {
      title: 'Escolha sua vibe',
      modes: [
        { emoji: '😀', name: 'Modo Amigos', description: 'Diversão e desafios leves' },
        { emoji: '❤️', name: 'Modo Casais', description: 'Romântico e picante' },
        { emoji: '🔥', name: 'Modo Quente', description: 'Ousado e arrojado' },
        { emoji: '💀', name: 'Modo Extremo', description: 'Apenas para os corajosos' },
      ],
    },
    languages: {
      title: 'Disponível em vários idiomas',
      description: 'Spin The Truth está disponível nos seguintes idiomas:',
    },
    download: {
      title: 'Baixe Agora',
      description: 'Obtenha o Spin The Truth no seu dispositivo móvel. Disponível no iOS.',
    },
    meta: {
      title: 'Spin The Truth - Jogue com Amigos | App iOS',
      description: 'Spin The Truth - Jogue com amigos ou parceiros. Conheçam-se melhor. Maximize a diversão. Disponível no iOS.',
      keywords: 'spin the truth, verdade ou desafio, aplicativo móvel, iOS, amigos, jogo de festa',
    },
  },
};

export const supportedLanguages: Language[] = ['en', 'pt', 'pt-BR', 'es', 'fr', 'de'];
export const defaultLanguage: Language = 'en';

export const languageNames: Record<Language, string> = {
  en: 'English',
  pt: 'Português',
  'pt-BR': 'Português (BR)',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
};
